import { useMemo } from 'react';
import { prop, pick } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { useComponentConfig } from 'js/hooks/component';
import { useGameConfigurationSelector } from 'js/store/hooks';
import { CATEGORY_TYPES, CATEGORY_VIEWS } from 'js/hooks/games/types';

import * as Types from '../../../types';
import { useDynamicReducerSelector } from '../../../store';

export const useGetFilterOptions = () => {
    const translate = useTranslations();
    const allGames = useGameConfigurationSelector(prop('games'));
    const { gamesFeed, favoriteGames } = useDynamicReducerSelector(pick(['gamesFeed', 'favoriteGames']));
    const { customFilters } = useComponentConfig<Types.IGamesGridConfig>();

    return useMemo(() => {
        const filterLabels = new Set<string>();
        const liveGamesAliases = new Set<string>();
        const filterProviders = new Set<string>();
        const filterCustomFields: { [key: string]: Set<string> } = {};
        const filterCategories: {
            name: string;
            feedName: string;
            pggBecauseYouPlayedGameName: string | undefined;
        }[] = [];

        gamesFeed?.gamesFeedCategories.forEach((category: Types.TGamesFeedCategoryParsed) => {
            let { categoryGames } = category.categoryConfiguration;

            if (category.categoryConfiguration.categoryType === CATEGORY_TYPES.favorites) {
                categoryGames = favoriteGames;
            }

            if (!categoryGames.length || category.categoryConfiguration.categoryView === CATEGORY_VIEWS.webcontent) {
                return;
            }

            if (category.categoryConfiguration.categoryView === CATEGORY_VIEWS.top) {
                return;
            }

            filterCategories.push({
                name: category.name,
                feedName: gamesFeed.name,
                pggBecauseYouPlayedGameName: category.categoryConfiguration.pggBecauseYouPlayedGameName
            });

            categoryGames.forEach((gameCode) => {
                const game = allGames[gameCode];
                const customFiltersArray = customFilters?.map((filter) => filter.CustomField) || [];

                if (game?.liveLaunchAlias) {
                    liveGamesAliases.add(game.liveLaunchAlias);
                }

                if (game?.gameProvider) {
                    filterProviders.add(game.gameProvider);
                }

                if (game?.gameLabels.length) {
                    game?.gameLabels.forEach((label) => {
                        label.name && label.isVisible && filterLabels.add(label.name);
                    });
                }

                if (game?.gameCustomFieldsData.length) {
                    game?.gameCustomFieldsData.forEach((field) => {
                        if (field.tpe !== 'text' || !customFiltersArray.includes(field.name) || !field.value) {
                            return;
                        }

                        filterCustomFields[field.name] = filterCustomFields[field.name] || new Set<string>();
                        filterCustomFields[field.name].add(field.value);
                    });
                }
            });
        });

        const providerOptions = Array.from(filterProviders).map((provider) => ({
            value: provider,
            label: translate(`playtech.mobile.system.gameprovider.${provider}`, provider)
        }));

        const labelOptions = Array.from(filterLabels).map((label) => ({
            value: label,
            label: translate(`playtech.mobile.system.gamelabel.${label}`, label)
        }));

        const categoryOptions = Array.from(filterCategories).map(({ name, feedName, pggBecauseYouPlayedGameName = '' }) => ({
            value: name,
            label: translate(`playtech.mobile.system.games-grid.category.${feedName}.${name}`, [pggBecauseYouPlayedGameName], name)
        }));

        const customFieldsOptions = Object.keys(filterCustomFields).map((key) => ({
            name: key,
            options: Array.from(filterCustomFields[key] || []).map((value: string) => ({
                value,
                label: translate(value, value)
            }))
        }));

        return { labelOptions, categoryOptions, providerOptions, customFieldsOptions };

    }, [allGames, customFilters, gamesFeed, translate, favoriteGames]);
};
